/**
 * Stereo PCM capture from a live WebRTC call.
 *
 * Taps the call's own tracks — the agent microphone (the RTCRtpSender track)
 * and the remote party (the RTCRtpReceiver track) — into one AudioWorklet that
 * resamples to 16 kHz and emits interleaved 16-bit PCM: channel 0 = agent,
 * channel 1 = customer. Separate channels give the STT provider perfect
 * speaker attribution.
 *
 * The AudioContext runs at the device rate (Firefox refuses to connect
 * MediaStream sources to a context at a different rate); resampling happens
 * in the worklet. Nothing here touches the call's audio path — the taps are
 * read-only and the worklet's output is muted.
 */

export const TARGET_RATE = 16_000
/** 100 ms of audio per chunk. */
const FRAMES_PER_CHUNK = TARGET_RATE / 10

const WORKLET_SOURCE = `
class Kc3StereoTap extends AudioWorkletProcessor {
  constructor(options) {
    super()
    this.ratio = sampleRate / ${TARGET_RATE}
    this.frames = ${FRAMES_PER_CHUNK}
    this.out = new Int16Array(this.frames * 2)
    this.n = 0
    this.pos = 0
    this.sum0 = 0
    this.sum1 = 0
  }
  process(inputs) {
    const input = inputs[0]
    if (!input || input.length === 0) return true
    const a = input[0]
    const b = input[1] || input[0]
    const len = a.length
    // Linear-interpolation resampler, carrying the fractional read position
    // across render quanta.
    while (this.pos < len) {
      const i = Math.floor(this.pos)
      const f = this.pos - i
      const j = Math.min(i + 1, len - 1)
      const s0 = a[i] + (a[j] - a[i]) * f
      const s1 = b[i] + (b[j] - b[i]) * f
      this.sum0 += s0 * s0
      this.sum1 += s1 * s1
      this.out[this.n * 2] = Math.max(-1, Math.min(1, s0)) * 0x7fff
      this.out[this.n * 2 + 1] = Math.max(-1, Math.min(1, s1)) * 0x7fff
      this.n++
      if (this.n === this.frames) {
        const buf = this.out.buffer.slice(0)
        this.port.postMessage({ pcm: buf, l0: Math.sqrt(this.sum0 / this.frames), l1: Math.sqrt(this.sum1 / this.frames) }, [buf])
        this.n = 0
        this.sum0 = 0
        this.sum1 = 0
      }
      this.pos += this.ratio
    }
    this.pos -= len
    return true
  }
}
registerProcessor('kc3-stereo-tap', Kc3StereoTap)
`

export interface StereoCapture {
  stop: () => void
  /** `mic|remote` track ids this capture reads. */
  key: string
}

export interface CaptureCallbacks {
  onChunk: (pcm: ArrayBuffer) => void
  /** RMS levels 0..1 per 100 ms — for the panel's activity meters. */
  onLevels?: (agent: number, customer: number) => void
}

let workletUrl: string | null = null

/**
 * The call's CURRENT audio tracks. A mid-call re-INVITE can leave an old,
 * dead transceiver in the connection next to the live one, so "the first
 * audio track" is not good enough: prefer a live track on a transceiver that
 * is actually sending / receiving, newest last.
 */
export function callAudioTracks(pc: RTCPeerConnection): { mic: MediaStreamTrack | null; remote: MediaStreamTrack | null } {
  const transceivers = typeof pc.getTransceivers === 'function' ? pc.getTransceivers() : []
  const activeDir = (t: RTCRtpTransceiver | undefined, want: 'send' | 'recv'): boolean => {
    const d = t?.currentDirection ?? t?.direction
    return !t || !d ? true : d === 'sendrecv' || d === (want === 'send' ? 'sendonly' : 'recvonly')
  }
  const pick = (cands: { track: MediaStreamTrack | null; tr: RTCRtpTransceiver | undefined }[], want: 'send' | 'recv'): MediaStreamTrack | null => {
    const audio = cands.filter((c): c is { track: MediaStreamTrack; tr: RTCRtpTransceiver | undefined } => c.track?.kind === 'audio')
    const best = audio.filter((c) => c.track.readyState === 'live' && c.tr?.currentDirection !== 'stopped' && activeDir(c.tr, want))
    return (best.at(-1) ?? audio.find((c) => c.track.readyState === 'live') ?? audio.at(-1))?.track ?? null
  }
  const mic = pick(pc.getSenders().map((sn) => ({ track: sn.track, tr: transceivers.find((t) => t.sender === sn) })), 'send')
  const remote = pick(pc.getReceivers().map((r) => ({ track: r.track, tr: transceivers.find((t) => t.receiver === r) })), 'recv')
  return { mic, remote }
}

/** Track ids a capture is bound to — compared after renegotiation. */
export function trackKey(pc: RTCPeerConnection): string {
  const { mic, remote } = callAudioTracks(pc)
  return `${mic?.id ?? '-'}|${remote?.id ?? '-'}`
}

export async function startStereoCapture(pc: RTCPeerConnection, cb: CaptureCallbacks): Promise<StereoCapture> {
  const { mic, remote } = callAudioTracks(pc)
  if (!mic && !remote) throw new Error('The call has no audio tracks yet.')
  const ctx = new AudioContext({ latencyHint: 'interactive' })
  if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
  // A suspended/interrupted context silently stops delivering audio.
  ctx.onstatechange = () => {
    if (ctx.state === 'suspended' || (ctx.state as string) === 'interrupted') void ctx.resume().catch(() => undefined)
  }
  workletUrl ??= URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }))
  await ctx.audioWorklet.addModule(workletUrl)

  const merger = ctx.createChannelMerger(2)
  // Each track gets its own MediaStream: a source node reads every track of
  // the stream it is given, and the channels must not be mixed.
  const sources: MediaStreamAudioSourceNode[] = []
  if (mic) {
    const s = ctx.createMediaStreamSource(new MediaStream([mic]))
    s.connect(merger, 0, 0)
    sources.push(s)
  }
  if (remote) {
    const s = ctx.createMediaStreamSource(new MediaStream([remote]))
    s.connect(merger, 0, 1)
    sources.push(s)
  }
  const tap = new AudioWorkletNode(ctx, 'kc3-stereo-tap', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1], channelCount: 2, channelCountMode: 'explicit', channelInterpretation: 'discrete' })
  tap.port.onmessage = (e: MessageEvent<{ pcm: ArrayBuffer; l0: number; l1: number }>) => {
    cb.onChunk(e.data.pcm)
    cb.onLevels?.(e.data.l0, e.data.l1)
  }
  // The graph only renders while it reaches the destination; a zero gain
  // keeps it running without playing anything back to the agent.
  const mute = ctx.createGain()
  mute.gain.value = 0
  merger.connect(tap)
  tap.connect(mute)
  mute.connect(ctx.destination)

  let stopped = false
  return {
    key: `${mic?.id ?? '-'}|${remote?.id ?? '-'}`,
    stop() {
      if (stopped) return
      stopped = true
      ctx.onstatechange = null
      tap.port.onmessage = null
      sources.forEach((s) => s.disconnect())
      merger.disconnect()
      tap.disconnect()
      mute.disconnect()
      void ctx.close().catch(() => undefined)
    },
  }
}
